import { type ArrayType, removeNulls } from '~/util'
import type {
  Community,
  CommunityConfig,
  FloorPlan,
  GuestCard,
  QuoteCategory,
  Unit,
} from '../fromSelfTour'
import type { API_Charge, FeeType, ID } from '../self-tour.service.types'

type Optional<T> = T | undefined

export function tourableUnits(
  _community: Optional<Community>,
  guestCard: Optional<GuestCard>
) {
  return guestCard?.tourableUnits ?? []
}

export function shownUnits(
  community: Optional<Community>,
  guestCard: Optional<GuestCard>
) {
  return !!guestCard?.shownUnitIds && !!community?.units
    ? guestCard.shownUnitIds
        .map((unitId) => community.units!.find((unit) => unit.id === unitId))
        .filter(removeNulls)
    : []
}

/**
 * Determine whether a unit can be unlocked during a tour. It's possible that a
 * unit may have a locke device but we actually want to hide it because it is no
 * longer available to rent.
 */
export function unitCanBeUnlocked(unit: Unit) {
  return (
    !!unit.lockDeviceId &&
    unit.isAvailableForTour &&
    // Model units should be unlockable regardless of the available flag but
    // other units only be unlockable if they are available for lease.
    ((unit.isAvailableForLease && !unit.isModel) || unit.isModel)
  )
}

export function interestedUnits(
  community: Optional<Community>,
  guestCard: Optional<GuestCard>
) {
  return !!guestCard?.interestedUnitIds && !!community?.units
    ? guestCard.interestedUnitIds
        .map((unitId) => community.units!.find((unit) => unit.id === unitId))
        .filter(removeNulls)
    : []
}

/**
 * Get quotes sorted by expiration date.
 */
export function quotes(guestCard: Optional<GuestCard>) {
  let quotes = guestCard?.quotes || []
  let sortedQuotes = quotes.slice()?.sort((a, b) => {
    const aExp = a?.expirationDate
    const bExp = b?.expirationDate
    if (aExp == null && bExp != null) return 1
    else if (aExp != null && bExp == null) return -1
    else if (aExp != null && bExp != null) {
      return new Date(bExp).getTime() - new Date(aExp).getTime()
    }
    return 0
  })
  return sortedQuotes
}

/**
 * Get the details for all quotes on the given guest card.
 */
export function quotesForUnit(
  guestCard: Optional<GuestCard>,
  unitId: Optional<ID>
) {
  return quotes(guestCard).filter((quote) => quote.unitId === unitId)
}

const filterCategoryItems = (
  items: API_Charge[],
  type: FeeType,
  unit: Unit
) => {
  if (!unit || !unit.quoteCategoryItemIds) {
    return []
  }
  return items.filter(
    (i) =>
      i.feeType === type &&
      Array.isArray(unit.quoteCategoryItemIds) &&
      unit.quoteCategoryItemIds.indexOf(i.id) > -1
  )
}

const findCategoryItemsByFeeType = (
  quoteCategories: QuoteCategory[] | undefined,
  type: FeeType,
  unit: Unit
) => {
  return (
    quoteCategories?.flatMap((c) => filterCategoryItems(c.items, type, unit)) ||
    []
  )
}

// TODO Remove?
export function quoteMonthlyCharges(
  community: Optional<Community>,
  unit?: Unit
) {
  if (!unit) return []
  return findCategoryItemsByFeeType(community?.quoteCategories, 'MONTHLY', unit)
}

// TODO Remove?
export function quoteRequiredCharges(
  community: Optional<Community>,
  unit?: Unit
) {
  if (!unit) return []
  return findCategoryItemsByFeeType(
    community?.quoteCategories,
    'REQUIRED',
    unit
  )
}

// TODO Remove?
export function quoteAdditionalCharges(
  community: Optional<Community>,
  unit?: Unit
) {
  if (!unit) return []
  return (
    community?.quoteCategories
      ?.map((category) => ({
        ...category,
        items: filterCategoryItems(category.items, 'NON_REQUIRED', unit),
      }))
      .sort((a, b) => {
        const scoreItem = (item: typeof a) => {
          if (item.name.indexOf('Application') > -1) {
            return 3
          } else if (item.name.indexOf('Pet') > -1) {
            return 2
          } else if (item.name.indexOf('Additional') > -1) {
            return 1
          } else {
            return 0
          }
        }

        return scoreItem(b) - scoreItem(a)
      }) || []
  )
}
export type AdditionalQuoteCharges = ArrayType<
  ReturnType<typeof quoteAdditionalCharges>
>

/**
 * Get the features from all units at a community.
 */
// TODO Rename communityUnitFeatures
export function allFeaturesForUnitsByName(community: Optional<Community>) {
  const units = community?.units || []
  const allUnitFeatures: string[] = []
  units.forEach((unit) => {
    unit.features.forEach((feature) => {
      if (feature.name && !allUnitFeatures.includes(feature.name))
        allUnitFeatures.push(feature.name)
    })
  })
  return allUnitFeatures
}

/**
 * Whether or not a tour should show the prices for each unit.
 *
 * We can show unit prices if:
 * 1) Variable rent is disabled
 * 2) Variable rent is enabled but the user has no quotes.
 */
export function showUnitPrices(
  config: Optional<CommunityConfig>,
  guestCard: Optional<GuestCard>
) {
  // We will most likey need to bring this logic back in the future.
  const isVariableRentEnabled = config?.isVariableRentEnabled
  const hasQuotes = !!guestCard?.quotes?.length
  return (
    // The global pricing toggle
    !!config?.showPrices &&
    // Or if the community uses variable rent pricing BUT this guest card
    // includes quotes, hide the pricing so they don't conflict with each
    // other (for example, if the user got a special deal that isn't reflected
    // in the standard pricing).
    (!isVariableRentEnabled || (isVariableRentEnabled && !hasQuotes))
  )
}

/**
 * The the amenities that a user should see on their tour (ie. the ones defined
 * on the guest card).
 */
// TODO Rename guestCardAmenities
export function communityAmenitiesFromGuestCardIds(
  community: Optional<Community>,
  guestCard: Optional<GuestCard>
) {
  return (
    community?.amenities?.filter((amenity) =>
      guestCard?.amenityIds?.includes(amenity.id)
    ) || []
  )
}

/**
 * Get the details of a specific floor plan
 */
export function floorPlan(community: Optional<Community>, id: ID) {
  return filterCommunityFloorPlans(community)?.find((f) => f.id === id)
}

export interface FloorPlanFilterOptions {
  price: number | null
  bedrooms: number | null
  bathrooms: number | null
  earliestMoveIn: string | null
  latestMoveIn: string | null
  features: string[] | null
  floor: number | null
}

export interface FloorPlanWithUnits extends FloorPlan {
  units: Unit[]
}

/**
 * Filter a community's floor plans.
 */
export function filterCommunityFloorPlans(
  community: Optional<Community>,
  filters: Partial<FloorPlanFilterOptions> = {}
): FloorPlanWithUnits[] {
  const units = community?.units || []
  const floorPlans = community?.floorPlans

  return (
    floorPlans?.reduce((acc: (FloorPlan & { units: Unit[] })[], fp) => {
      // Filter Floor Plans
      if (filters) {
        if (
          fp.bedrooms != null &&
          filters.bedrooms &&
          filters.bedrooms > fp.bedrooms
        )
          return acc
        if (
          fp.bathrooms != null &&
          filters.bathrooms &&
          filters.bathrooms > fp.bathrooms
        )
          return acc
      }
      // Filter Units
      const matchingUnits = units?.filter((unit) => {
        let unitFeatureNames = unit?.features
          ?.map((feature) => feature.name)
          .filter(removeNulls)

        if (unit.floorPlanId === fp.id) {
          if (filters) {
            if (
              unit.price != null &&
              filters.price &&
              filters.price < unit.price
            )
              return false

            if (
              unit.availableTime != null &&
              filters.earliestMoveIn &&
              filters.latestMoveIn &&
              !(
                new Date(filters.earliestMoveIn).getTime() <=
                  new Date(unit.availableTime).getTime() &&
                new Date(filters.latestMoveIn).getTime() >=
                  new Date(unit.availableTime).getTime()
              )
            )
              return false

            if (
              filters.features &&
              !unitFeatureNames.some((feature) =>
                filters.features!.includes(feature)
              )
            ) {
              return false
            }

            if (filters.floor != null && filters.floor !== unit.floor)
              return false

            return true
          } else {
            return true
          }
        } else {
          return false
        }
      })

      if (matchingUnits.length > 0) acc.push({ ...fp, units: matchingUnits })

      return acc
    }, []) || []
  )
}

/**
 * Whether or not a community requires signing a self tour agreement before then
 * can take their tour.
 */
export function selfTourRequiresAgreement(config: Optional<CommunityConfig>) {
  return !!config?.selfTourAgreementText
}

/**
 * Whether or not the user has previously checked in for their tour. Being
 * "checked in" means having completed the pre-tour questions.
 */
// TODO Rename userIsCheckedIn
export function checkedIn(guestCard: Optional<GuestCard>) {
  return (
    !!guestCard?.earliestMoveIn &&
    !!guestCard?.latestMoveIn &&
    !!guestCard?.maxPrice &&
    !!(guestCard?.bedBathIds?.length > 0)
  )
}
